import classNames from 'classnames';
import times from 'lodash/times';
import React from 'react';
import styles from './LoadingCards.module.scss';

export const EVENT_TYPE = 'event';
export const ORGANIZER_TYPE = 'organizer';
export const ORGANIZER_ARTICLE_TYPE = 'organizerArticle';

type CardType = typeof EVENT_TYPE | typeof ORGANIZER_TYPE | typeof ORGANIZER_ARTICLE_TYPE;

interface LoadingCardsProps {
    /*
     * Determines loading card style to display
     * list, grid, list-square
     */
    style: 'list' | 'grid' | 'list-square' | 'list-responsive';
    /*
     * The number of cards to be rendered
     */
    cardCount: number;
    /*
     * Determines loading card type to render
     * event, organizer
     */
    type: CardType;
    /**
     * Flipped changes the side the image
     * is rendered on
     */
    isFlipped?: boolean;
}

const EventLoadingCard = ({ isFlipped = false }) => (
    <div data-testid="consumerEventLoadingCard" className={styles.consumerEventLoadingCard}>
        {isFlipped ? null : (
            <div
                className={`${styles.consumerEventLoadingCardItem} ${styles.consumerEventLoadingCardMain}`}
            />
        )}
        <div className={styles.consumerEventLoadingCardAside}>
            <div className={styles.consumerEventLoadingCardItem} />
            <div className={styles.consumerEventLoadingCardItem} />
            <div className={styles.consumerEventLoadingCardItem} />
            <div className={styles.consumerEventLoadingCardItem} />
        </div>
        {isFlipped ? (
            <div
                className={`${styles.consumerEventLoadingCardItem} ${styles.consumerEventLoadingCardMain}`}
            />
        ) : null}
    </div>
);

const OrganizerLoadingCard = () => (
    <div data-testid="organizer-loading-card" className={styles.consumerOrgLoadingCard}>
        <div
            className={`${styles.consumerOrgLoadingCardItem} ${styles.consumerOrgLoadingCardMain}`}
        />
        <div className={styles.consumerOrgLoadingCardAside}>
            <div className={styles.consumerOrgLoadingCardItem} />
            <div className={styles.consumerOrgLoadingCardItem} />
            <div className={styles.consumerOrgLoadingCardItem} />
            <div className={styles.consumerOrgLoadingCardItem} />
        </div>
    </div>
);

const OrganizerArticleLoadingCard = () => (
    <div
        data-testid="organizer-article-loading-card"
        className={styles.consumerOrgArticleLoadingCard}
    >
        <div className={styles.consumerOrgArticleLoadingCardHeader}>
            <div className={styles.consumerOrgArticleLoadingCardHeaderTitle} />
            <div className={styles.consumerOrgArticleLoadingCardHeaderDescriptionLarge} />
            <div className={styles.consumerOrgArticleLoadingCardHeaderDescriptionSmall} />
        </div>
        <div className={styles.consumerOrgArticleLoadingCardBackground}>
            <div className={styles.consumerOrgArticleLoadingCardSummary}>
                <div className={styles.consumerOrgArticleLoadingCardSummaryOrgInfo}>
                    <div className={styles.consumerOrgArticleLoadingCardSummaryOrgImage} />
                    <div className={styles.consumerOrgArticleLoadingCardSummaryOrgTextContainer}>
                        <div className={styles.consumerOrgArticleLoadingCardSummaryOrgTitle} />
                        <div className={styles.consumerOrgArticleLoadingCardSummaryOrgSubtitle} />
                    </div>
                </div>
                <div className={styles.consumerOrgArticleLoadingCardSummaryTitleLarge} />
                <div className={styles.consumerOrgArticleLoadingCardSummaryTitleSmall} />
                <div className={styles.consumerOrgArticleLoadingCardSummaryDescriptionLarge} />
                <div className={styles.consumerOrgArticleLoadingCardSummaryDescriptionSmall} />
                <div className={styles.consumerOrgArticleLoadingCardSummaryCta} />
            </div>
        </div>
    </div>
);

const _getDeclaredCards = (cardCount: number, type: CardType, isFlipped?: boolean) =>
    times(cardCount, (index) => {
        let loadingCard = (
            <EventLoadingCard key={`consumer-event-loading-card-${index}`} isFlipped={isFlipped} />
        );

        if (type === ORGANIZER_TYPE) {
            loadingCard = <OrganizerLoadingCard key={`consumer-organizer-loading-card-${index}`} />;
        }

        if (type === ORGANIZER_ARTICLE_TYPE) {
            loadingCard = (
                <OrganizerArticleLoadingCard key={`consumer-org-article-loading-card-${index}`} />
            );
        }

        return loadingCard;
    });

/*
 * Rough implementation of a configurable loading state component for
 * event style cards on search experiences. Currently structured to support
 * a single column of cards of variable counts and styles.
 */
export const LoadingCards = ({
    style = 'list',
    type = EVENT_TYPE,
    cardCount = 5,
    isFlipped = false,
}: LoadingCardsProps) => {
    const cards = _getDeclaredCards(cardCount, type, isFlipped);

    const classes = classNames(
        styles.root,
        {
            [styles.consumerLoadingCardsList]: style === 'list',
            [styles.consumerLoadingCardsGrid]: style === 'grid',
            [styles.consumerLoadingCardsListSquare]: style === 'list-square',
            [styles.consumerLoadingCardsListResponsive]: style === 'list-responsive',
        },
        {
            [styles.consumerLoadingCardsListFlipped]: style === 'list' && isFlipped,
            [styles.consumerLoadingCardsGridFlipped]: style === 'grid' && isFlipped,
            [styles.consumerLoadingCardsListSquareFlipped]: style === 'list-square' && isFlipped,
            [styles.consumerLoadingCardsListResponsiveFlipped]:
                style === 'list-responsive' && isFlipped,
        },
    );

    return <section className={classes}>{cards}</section>;
};

export default LoadingCards;
